import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@availity/element';
import CarouselAngleLeft from '../../../static/assets/icons/carousel-angle-left.svg';
import CarouselAngleRight from '../../../static/assets/icons/carousel-angle-right.svg';

const ScalableCard = ({ title, description, visual }) => (
  <Box className="scalable-card">
    <Box className="scalable-card__visual">
      {visual}
    </Box>
    <Box className="scalable-card__content">
      <Typography className="scalable-card__title">
        {title}
      </Typography>
      <Typography className="scalable-card__description">
        {description}
      </Typography>
    </Box>
  </Box>
);

ScalableCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  visual: PropTypes.node.isRequired,
};

const AnimationVisual = ({ src, title }) => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hasLoaded, setHasLoaded] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasLoaded) {
          setHasLoaded(true);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [hasLoaded]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {hasLoaded && (
        <iframe
          ref={iframeRef}
          src={src}
          title={title}
          scrolling="no"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
};

const ScalableSection = () => {
  const [currentCard, setCurrentCard] = React.useState(0);
  const totalCards = 3;

  const handleNext = () => {
    setCurrentCard((prev) => (prev + 1) % totalCards);
  };

  const handlePrev = () => {
    setCurrentCard((prev) => (prev - 1 + totalCards) % totalCards);
  };

  const cards = [
    {
      title: "Future-Ready",
      description: "Easily integrate new APIs as your needs evolve.",
      visual: <AnimationVisual src="/static/assets/illustrations/Animation-Bundled-Loop.html" title="Future-Ready Animation" />
    },
    {
      title: "Elastic Infrastructure",
      description: "Handle millions of transactions without performance loss.",
      visual: <AnimationVisual src="/static/assets/illustrations/API-Marketplace-Elastic.html" title="Elastic Infrastructure Animation" />
    },
    {
      title: "High Availability",
      description: "99.9% uptime SLA for uninterrupted service.",
      visual: <AnimationVisual src="/static/assets/illustrations/API-Marketplace-Uptime.html" title="High Availability Animation" />
    }
  ];

  return (
  <Box
    sx={{
      width: 1,
      py: { xs: 8, md: 16.5 },
      background: '#000C30',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: { xs: 6, md: 11 },
    }}
  >
    <Box
      sx={{
        width: { xs: 1, lg: 976 },
        maxWidth: 976,
        px: { xs: 2, lg: 0 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography
        sx={{
          textAlign: 'center',
          color: '#91B0DA',
          fontSize: { xs: 18, md: 18 },
          fontFamily: 'Lato',
          fontWeight: 400,
          lineHeight: 26/18,
        }}
      >
        Scalable by Design
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: 'white',
          fontSize: { xs: 28, md: 36 },
          fontFamily: 'Montserrat',
          fontWeight: 600,
          lineHeight: 48/36,
        }}
      >
        Built to Scale with Your Business
      </Typography>
    </Box>

    {/* Desktop view - all cards */}
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 3,
      }}
    >
      {cards.map((card) => (
        <ScalableCard
          key={card.title}
          title={card.title}
          description={card.description}
          visual={card.visual}
        />
      ))}
    </Box>

    {/* Mobile/Tablet view - carousel */}
    <Box
      sx={{
        width: { xs: 1, lg: 1200 },
        maxWidth: 1200,
        px: { xs: 2, lg: 0 },
        display: { xs: 'flex', lg: 'none' },
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        position: 'relative',
      }}
    >
      {/* Left Arrow */}
      <Box
        onClick={handlePrev}
        sx={{
          cursor: 'pointer',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={CarouselAngleLeft} alt="Previous" width="42" height="42" />
      </Box>

      {/* Card */}
      <Box sx={{ width: 380, flexShrink: 0 }}>
        <ScalableCard
          title={cards[currentCard].title}
          description={cards[currentCard].description}
          visual={cards[currentCard].visual}
        />
      </Box>

      {/* Right Arrow */}
      <Box
        onClick={handleNext}
        sx={{
          cursor: 'pointer',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={CarouselAngleRight} alt="Next" width="42" height="42" />
      </Box>
    </Box>
  </Box>
  );
};

export default ScalableSection;